<script setup lang="ts">
import {
  Schema,
  type SchemaOptions,
  type SchemaRenderingProps,
} from '@scalar/blocks/schema'
import { ScalarMarkdown } from '@scalar/components/markdown'
import type {
  AsyncApiDocument,
  AsyncApiMessageObject,
} from '@scalar/types/asyncapi/3.1'
import type { WorkspaceEventBus } from '@scalar/workspace-store/events'
import { getResolvedRef } from '@scalar/workspace-store/helpers/get-resolved-ref'
import type { TraversedAsyncApiMessage } from '@scalar/workspace-store/schemas/navigation'
import { computed, nextTick, ref, useId, useTemplateRef, watch } from 'vue'

import { Anchor } from '@/components/Anchor'
import { SectionAccordion, SectionHeaderTag } from '@/components/Section'
import { useDocumentOutline } from '@/features/document-outline'
import {
  getAsyncApiMessageHeadersSchema,
  getAsyncApiMessagePayloadSchema,
} from '@/helpers/get-async-api-message-payload-schema'
import { useIntersection } from '@/hooks/use-intersection'

import AsyncApiLabels from './AsyncApiLabels.vue'
import {
  resolveSchemaRenderOptions,
  type AsyncApiSchemaRenderOptions,
} from './helpers/async-api-render-options'
import { getChannelServerLabels } from './helpers/get-async-api-labels'
import { getCorrelationIdTarget } from './helpers/get-correlation-id-target'
import { getGeneratedPayloadExample } from './helpers/get-generated-payload-example'
import { pickHeading } from './helpers/pick-heading'
import {
  resolveAsyncApiChannel,
  resolveAsyncApiMessage,
} from './helpers/resolve-async-api-nodes'
import MessageExamples from './MessageExamples.vue'

/** Subset of the configuration the shared `Schema` renderer needs. */
type SchemaRenderOptions = AsyncApiSchemaRenderOptions

const {
  message,
  document,
  eventBus,
  options,
  expandedItems = {},
  parent = 'operation',
  scrollTargetId = '',
  expansion,
} = defineProps<
  {
    /** Direct channel messages sit beneath the channel catalog heading. */
    parent?: 'channel' | 'operation'
    message: TraversedAsyncApiMessage
    document: AsyncApiDocument
    eventBus: WorkspaceEventBus | null
    options?: Partial<SchemaRenderOptions>
    /** Map of navigation item id to expanded state, shared with the sidebar. */
    expandedItems?: Record<string, boolean>
  } & SchemaRenderingProps
>()

const headerId = useId()
const section = useTemplateRef<HTMLElement>('section')

useIntersection(section, () =>
  eventBus?.emit('intersecting:nav-item', { id: message.id }),
)

/**
 * Resolve the message from the channel it lives on. The navigation entry only
 * carries the identifying keys, so we walk `document.channels[channelName].messages`.
 */
const resolvedMessage = computed(() =>
  resolveAsyncApiMessage(document, message.channelName, message.messageName),
)

/** Heading prefers the human-friendly title, falling back to the message map key. */
const headingText = computed(() =>
  pickHeading(resolvedMessage.value?.title, message.title, message.messageName),
)

const description = computed(
  () =>
    resolvedMessage.value?.description || resolvedMessage.value?.summary || '',
)

/**
 * Protocol keys declared directly on the message's protocol-specific `bindings`
 * (for example `ws`, `kafka`). The bindings object may be a `$ref`, so it is resolved
 * before reading the keys. Keys are lowercased to match the server protocols (which the
 * helper normalizes), so the union below de-duplicates case-insensitively.
 */
const messageBindingProtocols = computed(() => {
  const bindings = resolvedMessage.value?.bindings
  if (!bindings) {
    return []
  }
  const resolved = getResolvedRef(bindings)
  return Object.entries(resolved ?? {})
    .filter(([, value]) => value != null)
    .map(([protocol]) => protocol.toLowerCase())
})

/**
 * Protocol labels for the message. A message is carried over whatever protocols its
 * channel's servers speak, so we start from the channel's server protocols and union in
 * any extra protocols the message declares its own bindings for. Without this a
 * multi-protocol message would only surface the single protocol it happens to declare a
 * binding for (or none at all).
 */
const protocolLabels = computed(() => {
  const channel = resolveAsyncApiChannel(document, message.channelName)
  const { protocols } = getChannelServerLabels(document, channel)
  return [...new Set([...protocols, ...messageBindingProtocols.value])]
})

/** Payload schema, unwrapped from `$ref`s and Multi Format Schema wrappers. */
const payloadSchema = computed(() =>
  resolvedMessage.value
    ? getAsyncApiMessagePayloadSchema(resolvedMessage.value)
    : undefined,
)

/** Header schema, unwrapped the same way as the payload. */
const headersSchema = computed(() =>
  resolvedMessage.value
    ? getAsyncApiMessageHeadersSchema(resolvedMessage.value)
    : undefined,
)

const correlationId = computed<
  Exclude<AsyncApiMessageObject['correlationId'], { $ref: string }>
>(() => getResolvedRef(resolvedMessage.value?.correlationId))
const correlationTarget = computed<ReturnType<typeof getCorrelationIdTarget>>(
  () =>
    resolvedMessage.value && correlationId.value?.location
      ? getCorrelationIdTarget(
          resolvedMessage.value,
          correlationId.value.location,
        )
      : undefined,
)
const correlationBreadcrumb = computed<string[] | undefined>(() =>
  correlationTarget.value
    ? [
        message.id,
        correlationTarget.value.section,
        ...correlationTarget.value.path,
      ]
    : undefined,
)
const correlationTargetId = computed<string | undefined>(() =>
  correlationBreadcrumb.value?.join('.'),
)
const correlationHref = computed<string | undefined>(() =>
  correlationTargetId.value
    ? `#${encodeURIComponent(correlationTargetId.value)}`
    : undefined,
)
const localScrollTarget = ref<string>('')
const activeScrollTarget = computed<string>(
  () => scrollTargetId || localScrollTarget.value,
)

/** The event bus expands lazy parents; standalone messages can reveal their own schema instead. */
const onCorrelationLink = (event: MouseEvent): void => {
  const id = correlationTargetId.value
  if (!id) {
    return
  }
  event.preventDefault()
  if (eventBus) {
    eventBus.emit('scroll-to:nav-item', { id })
    return
  }
  localScrollTarget.value = id
  expansion?.commitPath(id)
  void nextTick(() => {
    const target = section.value?.ownerDocument.getElementById(id)
    target?.scrollIntoView({ block: 'start' })
    target?.focus({ preventScroll: true })
  })
}

/** Fill in defaults so the shared Schema renderer always receives a complete options object. */
const schemaOptions = computed<SchemaOptions>(() => ({
  hideReadOnly: false,
  ...resolveSchemaRenderOptions(options),
  linkablePropertyPath: correlationBreadcrumb.value,
}))

/**
 * Accordion open state. Kept locally so clicking the header always toggles
 * immediately, and seeded/synced from the shared sidebar expansion map so
 * expanding the message in the sidebar (or deep-linking to it) opens it here too.
 */
const isExpanded = ref(expandedItems[message.id] ?? false)

watch(
  () => expandedItems[message.id],
  (value) => {
    if (value !== undefined) {
      isExpanded.value = value
    }
  },
)

/** Toggling the accordion updates local state and keeps the sidebar in sync. */
const onToggle = (open: boolean) => {
  isExpanded.value = open
  eventBus?.emit('toggle:nav-item', { id: message.id, open })
}

// Computed lazily when the expanded accordion renders its examples, then cached until the message changes.
const generatedPayload = computed<unknown>(() =>
  resolvedMessage.value
    ? getGeneratedPayloadExample(resolvedMessage.value)
    : undefined,
)

const { level: headingLevel } = useDocumentOutline(
  parent === 'channel' ? 'channelMessage' : 'message',
)
</script>

<template>
  <div
    :id="message.id"
    ref="section"
    class="message">
    <SectionAccordion
      class="message-accordion [&_.section-accordion-content-card]:bg-b-1 border [&_.section-accordion-content-card]:rounded-b-lg [&_.section-accordion-content-card]:p-4 [&_.section-accordion-header]:px-4 [&_.section-accordion-header]:py-3"
      :modelValue="isExpanded"
      @update:modelValue="onToggle">
      <template #title>
        <Anchor
          @copyAnchorUrl="
            () => eventBus?.emit('copy-url:nav-item', { id: message.id })
          ">
          <span class="message-heading">
            <SectionHeaderTag
              :id="headerId"
              class="message-title"
              :level="headingLevel">
              {{ headingText }}
            </SectionHeaderTag>
            <AsyncApiLabels :protocols="protocolLabels" />
          </span>
        </Anchor>
      </template>

      <div class="message-layout">
        <div
          v-if="description || correlationId || headersSchema || payloadSchema"
          class="message-details min-w-0">
          <ScalarMarkdown
            v-if="description"
            class="message-description"
            :value="description"
            withImages />

          <div
            v-if="correlationId"
            class="message-schema">
            <div class="message-schema-title">Correlation ID</div>
            <ScalarMarkdown
              v-if="correlationId.description"
              :value="correlationId.description"
              withImages />
            <a
              v-if="correlationHref"
              class="text-c-accent mt-2 block w-fit max-w-full rounded underline underline-offset-2 focus-visible:outline"
              :href="correlationHref"
              @click="onCorrelationLink">
              <code class="break-all">{{ correlationId.location }}</code>
            </a>
            <code
              v-else-if="correlationId.location"
              class="mt-2 block break-all"
              >{{ correlationId.location }}</code
            >
          </div>

          <div
            v-if="headersSchema"
            :id="`${message.id}.headers`"
            class="message-schema"
            tabindex="-1">
            <div class="message-schema-title">Headers</div>
            <Schema
              :breadcrumb="[message.id, 'headers']"
              class="ps-8"
              compact
              :eventBus="eventBus"
              :expansion="expansion"
              name="Headers"
              noncollapsible
              :options="schemaOptions"
              :schema="headersSchema"
              :scrollTargetId="activeScrollTarget"
              :specificationExtension="specificationExtension" />
          </div>

          <div
            v-if="payloadSchema"
            :id="`${message.id}.payload`"
            class="message-schema"
            tabindex="-1">
            <div class="message-schema-title">Payload</div>
            <Schema
              :breadcrumb="[message.id, 'payload']"
              class="ps-8"
              compact
              :eventBus="eventBus"
              :expansion="expansion"
              name="Payload"
              noncollapsible
              :options="schemaOptions"
              :schema="payloadSchema"
              :scrollTargetId="activeScrollTarget"
              :specificationExtension="specificationExtension" />
          </div>
        </div>
        <MessageExamples
          class="message-examples"
          :examples="resolvedMessage?.examples"
          :generatedPayload="isExpanded ? generatedPayload : undefined" />
      </div>
    </SectionAccordion>
  </div>
</template>

<style scoped>
.message {
  scroll-margin-top: var(--refs-viewport-offset);
}
.message-title {
  font-size: var(--scalar-heading-4);
  font-weight: var(--scalar-semibold);
  color: var(--scalar-color-1);
}
/*
 * Lay the heading and protocol labels out on one wrapping row. The labels live inside the
 * Anchor slot so the copy-anchor "#" button (absolutely positioned just past the end of the
 * slot content on hover) trails after the last pill instead of overlapping the first one.
 */
.message-heading {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.message-description {
  padding-bottom: 4px;
  text-align: left;
}
.message-schema {
  margin-top: 12px;
}
.message-schema:first-child {
  margin-top: 0;
}
.message-schema-title {
  font-size: var(--scalar-font-size-2);
  font-weight: var(--scalar-semibold);
  color: var(--scalar-color-1);
  padding-bottom: 8px;
  border-bottom: var(--scalar-border-width) solid var(--scalar-border-color);
  margin-bottom: 8px;
}
.message-layout {
  display: grid;
  gap: 16px;
}
/* Keep examples beside the schema when the reference has room, including embedded layouts. */
@container references-container (min-width: 900px) {
  .message-layout:has(> .message-details):has(> .message-examples) {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    align-items: start;
    gap: 24px;
  }
}
</style>
